import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useState
} from "react";
import { Spin } from "antd";
import { message } from "utils/ciq-message";
import { useHistory, useLocation } from "react-router-dom";
import { AppLogoIcon } from "components/svg-icons";
import useValidateUrlToken, {
  TokenValidationResult
} from "hooks/useValidateUrlToken";
import { getAccessToken, isAuthSessionActiveInTab } from "services/auth";
import { stripProjectDashboardTransientParams } from "utils/project-invite-redirect";
export type TokenValidationContextValue = TokenValidationResult & {
  isResetPasswordSessionExpired: boolean;
  isSetupAccountSessionExpired: boolean;
  isEnvAdminFromToken: boolean;
};

function isEnvAdminDecodedToken(
  decoded: TokenValidationResult["decodedToken"]
) {
  return (
    decoded?.["https://hasura.io/jwt/claims"]?.["x-hasura-default-role"] ===
    "env_admin"
  );
}

const TokenValidationContext =
  createContext<TokenValidationContextValue | null>(null);

export function useTokenValidation(): TokenValidationContextValue | null {
  return useContext(TokenValidationContext);
}

type TokenValidationGateProps = {
  children: ReactNode;
  skip?: boolean;
};

const MESSAGE_STYLE = { marginTop: 150 };

const CANCELLED_INVITE_STATUSES = new Set([
  "USER_INVITE_CANCELLED",
  "PROJECT_USER_INVITE_CANCELLED"
]);

const RESET_PASSWORD_ALREADY_SET_STATUSES = new Set([
  "INVITATION_ALREADY_ACCEPTED",
  "USER_NOT_ACCEPTED_SUBSCRIPTION_INVITE",
  "USER_NOT_ACCEPTED_PROJECT_INVITE"
]);

function normalizeValidationStatus(status: string | null): string | null {
  if (!status) {
    return null;
  }
  return status.trim().toUpperCase();
}

function TokenValidationGate({ children, skip }: TokenValidationGateProps) {
  const location = useLocation();
  const projectId = useMemo(() => {
    const searchProjectId = new URLSearchParams(location.search).get(
      "projectId"
    );
    if (searchProjectId?.trim()) {
      return searchProjectId.trim();
    }

    const hash = location.hash.startsWith("#")
      ? location.hash.slice(1)
      : location.hash;
    const hashProjectId = new URLSearchParams(
      hash.startsWith("?") ? hash : `?${hash}`
    ).get("projectId");
    return hashProjectId?.trim() || undefined;
  }, [location.search, location.hash]);

  const validation = useValidateUrlToken({
    skip,
    search: location.search,
    hash: location.hash,
    projectId
  });
  const history = useHistory();
  const [isResetPasswordSessionExpired, setResetPasswordSessionExpired] =
    useState(false);
  const [isSetupAccountSessionExpired, setSetupAccountSessionExpired] =
    useState(false);

  const isResetPasswordRoute = location.pathname === "/reset_password";
  const isSetupAccountRoute = location.pathname === "/set_password";

  const isEnvAdminFromToken = useMemo(
    () => isEnvAdminDecodedToken(validation.decodedToken),
    [validation.decodedToken]
  );
  const normalizedValidationStatus = useMemo(
    () => normalizeValidationStatus(validation.validationStatus),
    [validation.validationStatus]
  );
  const isCancelledInviteStatus = useMemo(
    () =>
      normalizedValidationStatus
        ? CANCELLED_INVITE_STATUSES.has(normalizedValidationStatus)
        : false,
    [normalizedValidationStatus]
  );

  useEffect(() => {
    if (validation.isLoading) {
      return;
    }
    if (!skip && (validation.error || isCancelledInviteStatus)) {
      return;
    }

    // Keep invite params until the user is signed in so the login bounce can
    // preserve the full post-login destination (CIQ-5973).
    if (!getAccessToken() || !isAuthSessionActiveInTab()) {
      return;
    }

    const cleaned = stripProjectDashboardTransientParams(
      location.pathname,
      location.search,
      location.hash
    );
    if (!cleaned) {
      return;
    }

    history.replace({
      pathname: cleaned.pathname,
      search: cleaned.search,
      hash: cleaned.hash
    });
  }, [
    history,
    isCancelledInviteStatus,
    location.hash,
    location.pathname,
    location.search,
    skip,
    validation.error,
    validation.isLoading
  ]);

  useEffect(() => {
    if (validation.isTokenExpired) {
      message.error({
        content: validation.validationMessage,
        style: MESSAGE_STYLE,
        duration: 10
      });
    }
  }, [validation.validationMessage, validation.isTokenExpired]);

  useEffect(() => {
    if (!isCancelledInviteStatus) {
      return;
    }

    message.error({
      content:
        validation.validationMessage || "This invite has been cancelled.",
      style: MESSAGE_STYLE,
      duration: 10
    });
  }, [isCancelledInviteStatus, validation.validationMessage]);

  useEffect(() => {
    if (!isResetPasswordRoute || skip) {
      setResetPasswordSessionExpired(false);
      return;
    }

    if (validation.isLoading) return;

    const validationStatus = normalizedValidationStatus;

    if (validationStatus === "USER_ACCOUNT_SETUP_NOT_DONE") {
      setResetPasswordSessionExpired(false);
      return;
    }

    if (validationStatus && CANCELLED_INVITE_STATUSES.has(validationStatus)) {
      setResetPasswordSessionExpired(false);
      return;
    }

    if (
      validationStatus &&
      RESET_PASSWORD_ALREADY_SET_STATUSES.has(validationStatus)
    ) {
      setResetPasswordSessionExpired(true);
      message.success({
        content: (
          <div>
            Password has already been set.{" "}
            <span>
              <a href="/" className="underline">
                Click here
              </a>
            </span>{" "}
            to login.
          </div>
        ),
        duration: 60
      });
      return;
    }

    if (validation.isValidated && validationStatus) {
      setResetPasswordSessionExpired(true);
      message.success(validation.validationMessage);
      history.push("/");
    }
  }, [
    history,
    isResetPasswordRoute,
    skip,
    normalizedValidationStatus,
    validation.isLoading,
    validation.validationMessage,
    validation.isValidated
  ]);

  useEffect(() => {
    if (!isSetupAccountRoute || skip) {
      setSetupAccountSessionExpired(false);
      return;
    }

    if (validation.isLoading) return;

    const validationStatus = normalizedValidationStatus;

    if (validationStatus === "USER_ACCOUNT_SETUP_NOT_DONE") {
      setSetupAccountSessionExpired(false);
      return;
    }

    if (validationStatus && CANCELLED_INVITE_STATUSES.has(validationStatus)) {
      setSetupAccountSessionExpired(false);
      return;
    }

    if (validationStatus === "USER_ACCOUNT_SETUP_DONE") {
      setSetupAccountSessionExpired(true);
      message.success({
        content: (
          <div>
            Password has already been set{" "}
            <span>
              <a
                href={isEnvAdminFromToken ? "/admin/#/" : "/"}
                className="underline"
              >
                Click here
              </a>
            </span>{" "}
            to Login.
          </div>
        ),
        duration: 60
      });
      return;
    }

    if (validation.isValidated && validationStatus) {
      setSetupAccountSessionExpired(true);
      message.success(validation.validationMessage);
      history.push("/");
    }
  }, [
    history,
    isSetupAccountRoute,
    skip,
    normalizedValidationStatus,
    validation.isLoading,
    validation.isValidated,
    validation.validationMessage,
    isEnvAdminFromToken
  ]);

  const contextValue = useMemo(
    (): TokenValidationContextValue => ({
      ...validation,
      isResetPasswordSessionExpired,
      isSetupAccountSessionExpired,
      isEnvAdminFromToken
    }),
    [
      validation,
      isResetPasswordSessionExpired,
      isSetupAccountSessionExpired,
      isEnvAdminFromToken
    ]
  );

  if (validation.isLoading) {
    return (
      <div className="flex min-w-full min-h-screen items-center justify-center">
        <Spin />
      </div>
    );
  }

  if ((!skip && validation.error) || isCancelledInviteStatus) {
    return (
      <div className="flex min-w-full min-h-screen bg-ciq-neutral-50 items-start justify-center p-2">
        <div className="flex flex-col p-8 shadow-[0px_5px_25px_rgba(0,0,0,0.1)] w-[400px] h-32 rounded-md">
          <div className="m-auto block">
            <AppLogoIcon />
          </div>
        </div>
      </div>
    );
  }

  return (
    <TokenValidationContext.Provider value={contextValue}>
      {children}
    </TokenValidationContext.Provider>
  );
}

export default TokenValidationGate;
